import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Instagram, ArrowUpRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IG_URL = "https://www.instagram.com/instinct.kravmaga";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: [0.33, 1, 0.68, 1] },
};

export const InstagramSection = () => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/instagram/feed?limit=6`)
      .then((res) => setItems(res.data.items || []))
      .catch(() => setItems([]));
  }, []);

  return (
    <section
      id="instagram"
      data-testid="instagram-section"
      className="relative px-6 md:px-16 lg:px-24 py-12 md:py-16"
    >
      <div className="section-divider mb-10 md:mb-14" />

      <motion.div {...fadeUp} className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
        <div>
          <div className="brass-line mb-4" />
          <span className="text-[10px] tracking-[0.3em] uppercase text-sig font-['IBM_Plex_Sans'] block mb-3">
            Follow the Movement
          </span>
          <h2 className="font-['Oswald'] text-3xl md:text-4xl lg:text-5xl font-bold uppercase tracking-tight leading-[0.95] text-ink">
            On <span className="text-sig">Instagram</span>
          </h2>
        </div>
        <a
          data-testid="instagram-follow-btn"
          href={IG_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-3 border border-sig/40 px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] text-sig hover:bg-sig/10 hover:text-ink transition-colors font-['IBM_Plex_Sans'] self-start md:self-auto"
        >
          <Instagram className="w-4 h-4" />
          @instinct.kravmaga
        </a>
      </motion.div>

      {items.length > 0 ? (
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.15 }}
          data-testid="instagram-feed-grid"
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2"
        >
          {items.map((item) => (
            <a
              key={item.id}
              href={item.permalink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-post"
              className="group relative aspect-square overflow-hidden border border-line hover:border-sig/50 transition-colors"
            >
              <img
                src={item.image_url}
                alt={item.caption ? item.caption.slice(0, 80) : "Instagram post"}
                loading="lazy"
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500"
              />
              <div className="absolute inset-0 bg-page/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                <span className="text-[10px] leading-snug text-ink font-['IBM_Plex_Sans'] line-clamp-3">
                  {item.caption ? item.caption.slice(0, 90) : "View on Instagram"}
                </span>
                <ArrowUpRight className="w-4 h-4 text-sig absolute top-3 right-3" />
              </div>
            </a>
          ))}
        </motion.div>
      ) : (
        <motion.a
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.15 }}
          href={IG_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="instagram-cta-banner"
          className="group flex items-center justify-between border border-line hover:border-sig/50 transition-colors p-6 md:p-8"
        >
          <div className="flex items-center gap-5">
            <Instagram className="w-8 h-8 text-sig" />
            <div>
              <p className="font-['Oswald'] text-lg md:text-xl font-bold uppercase tracking-tight text-ink">
                Training clips. Real techniques. No fluff.
              </p>
              <p className="text-xs text-mute font-['IBM_Plex_Sans'] mt-1">
                See what a session at INSTINCT actually looks like.
              </p>
            </div>
          </div>
          <ArrowUpRight className="w-6 h-6 text-sig group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
        </motion.a>
      )}
    </section>
  );
};
